
import { SQLModule } from '../types';

export const SQL_INTERMEDIATE_MODULES: SQLModule[] = [
  {
    id: 'sql-intermediate-1',
    title: 'Aggregate Functions and GROUP BY',
    description: 'Learn to use aggregate functions and group data for analysis.',
    content: `# Aggregate Functions and GROUP BY

Aggregate functions perform calculations on multiple rows and return a single result. The GROUP BY clause groups rows with the same values.

## Common Aggregate Functions

### COUNT()
Counts the number of rows:
\`\`\`sql
SELECT COUNT(*) FROM users;
SELECT COUNT(email) FROM users; -- Excludes NULL values
\`\`\`

### SUM(), AVG(), MIN(), MAX()
\`\`\`sql
SELECT SUM(salary) FROM employees;
SELECT AVG(age) FROM users;
SELECT MIN(price), MAX(price) FROM products;
\`\`\`

## GROUP BY Clause

Groups rows by one or more columns:

\`\`\`sql
-- Count users by department
SELECT department, COUNT(*) as user_count
FROM users
GROUP BY department;

-- Average salary by department
SELECT department, AVG(salary) as avg_salary
FROM employees
GROUP BY department;
\`\`\`

## HAVING Clause

HAVING filters groups (unlike WHERE which filters rows):

\`\`\`sql
-- Departments with more than 5 employees
SELECT department, COUNT(*) as employee_count
FROM employees
GROUP BY department
HAVING COUNT(*) > 5;
\`\`\`

## Complex Examples

\`\`\`sql
-- Monthly sales totals for 2024
SELECT 
    MONTH(order_date) as month,
    SUM(total_amount) as monthly_sales,
    COUNT(*) as order_count
FROM orders
WHERE YEAR(order_date) = 2024
GROUP BY MONTH(order_date)
ORDER BY month;
\`\`\``,
    duration: '50 min',
    estimatedTime: 50,
    difficulty: 'intermediate',
    order: 1
  },
  {
    id: 'sql-intermediate-2',
    title: 'Table Joins',
    description: 'Master different types of joins to combine data from multiple tables.',
    content: `# Table Joins

Joins are used to combine data from multiple tables based on related columns.

## Types of Joins

### INNER JOIN
Returns records that have matching values in both tables:

\`\`\`sql
SELECT users.name, orders.total_amount
FROM users
INNER JOIN orders ON users.id = orders.user_id;
\`\`\`

### LEFT JOIN (LEFT OUTER JOIN)
Returns all records from the left table and matched records from the right table:

\`\`\`sql
SELECT users.name, orders.total_amount
FROM users
LEFT JOIN orders ON users.id = orders.user_id;
\`\`\`

### RIGHT JOIN (RIGHT OUTER JOIN)
Returns all records from the right table and matched records from the left table:

\`\`\`sql
SELECT users.name, orders.total_amount
FROM users
RIGHT JOIN orders ON users.id = orders.user_id;
\`\`\`

### FULL OUTER JOIN
Returns all records when there's a match in either table:

\`\`\`sql
SELECT users.name, orders.total_amount
FROM users
FULL OUTER JOIN orders ON users.id = orders.user_id;
\`\`\`

## Multiple Table Joins

\`\`\`sql
SELECT 
    u.name as customer_name,
    o.order_date,
    p.product_name,
    oi.quantity,
    p.price
FROM users u
INNER JOIN orders o ON u.id = o.user_id
INNER JOIN order_items oi ON o.id = oi.order_id
INNER JOIN products p ON oi.product_id = p.id;
\`\`\`

## Self Joins

Joining a table with itself:

\`\`\`sql
SELECT 
    e.name as employee,
    m.name as manager
FROM employees e
LEFT JOIN employees m ON e.manager_id = m.id;
\`\`\``,
    duration: '60 min',
    estimatedTime: 60,
    difficulty: 'intermediate',
    order: 2
  }
];
